import React, { useMemo, useState } from "react";
import { Alert, Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import warehouseBinServices from "app/services/warehouse-bin-services";

// Las posiciones que existen pero que nadie ubicó en el plano. Van en una
// bandeja aparte en vez de apilarse en el origen, que las volvería invisibles.
const UnplacedTray = ({ bins, canEdit, onChanged, centreId }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [placing, setPlacing] = useState(false);

    const structured = useMemo(() => bins.filter((bin) => bin.structured), [bins]);
    const unstructured = useMemo(() => bins.filter((bin) => !bin.structured), [bins]);

    // Auto-ubicar usa la estructura del código: cada pasillo es una fila, cada
    // rack una columna. No adivina la forma real de la bodega, pero deja un
    // plano ordenado que después se arrastra a gusto — que es mucho mejor punto
    // de partida que ubicar 300 posiciones a mano.
    const autoPlace = async () => {
        if (!structured.length) return;

        const aisles = Array.from(new Set(structured.map((bin) => `${bin.zone || ""}|${bin.aisle || ""}`))).sort();

        const updates = [];
        aisles.forEach((aisleKey, rowIndex) => {
            const aisleBins = structured.filter((bin) => `${bin.zone || ""}|${bin.aisle || ""}` === aisleKey);
            const racks = Array.from(new Set(aisleBins.map((bin) => bin.rack || ""))).sort();

            racks.forEach((rack, colIndex) => {
                aisleBins
                    .filter((bin) => (bin.rack || "") === rack)
                    .forEach((bin) => {
                        updates.push({ id: bin.id, layout_x: colIndex, layout_y: rowIndex });
                    });
            });
        });

        try {
            setPlacing(true);
            await warehouseBinServices.bulkLayout(centreId, updates);
            enqueueSnackbar(`${updates.length} posiciones ubicadas en el plano`, { variant: "success" });
            onChanged();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudieron ubicar", { variant: "error" });
        } finally {
            setPlacing(false);
        }
    };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Box>
                    <Typography variant="h6" sx={{ mb: 0 }}>
                        Sin ubicar en el plano ({bins.length})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Existen y se pueden usar, pero no aparecen en el tablero hasta que tengan una posición.
                    </Typography>
                </Box>

                {canEdit && structured.length > 0 && (
                    <LoadingButton
                        size="small"
                        variant="outlined"
                        loading={placing}
                        startIcon={<AutoFixHighOutlinedIcon />}
                        onClick={autoPlace}
                    >
                        Auto-ubicar {structured.length}
                    </LoadingButton>
                )}
            </Stack>

            {structured.length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    {structured.slice(0, 60).map((bin) => (
                        <Chip key={bin.id} size="small" label={bin.code} variant="outlined" />
                    ))}
                    {structured.length > 60 && (
                        <Chip size="small" label={`+${structured.length - 60} más`} variant="outlined" />
                    )}
                </Stack>
            )}

            {unstructured.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {unstructured.length} posiciones llegaron del ERP como texto libre y no se pudieron
                        estructurar. No se les puede deducir el pasillo ni el rack, así que hay que mapearlas a mano.
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {unstructured.slice(0, 30).map((bin) => (
                            <Tooltip key={bin.id} title={bin.external_ref || bin.code} arrow>
                                <Chip size="small" color="warning" label={bin.code} variant="outlined" />
                            </Tooltip>
                        ))}
                        {unstructured.length > 30 && (
                            <Chip size="small" label={`+${unstructured.length - 30} más`} variant="outlined" />
                        )}
                    </Stack>
                </Alert>
            )}
        </Box>
    );
};

export default UnplacedTray;
